import api from '../api';

// Vendor API service
export const vendorApi = {
  // Vendor authentication (demo mode)
  login: async (email, password) => {
    try {
      const response = await api.post('/vendors/auth/login', {
        email,
        password
      });
      return response.data;
    } catch (error) {
      console.error('Vendor login error:', error);
      throw error;
    }
  },

  // Get vendor profile
  getProfile: async (vendorId) => {
    try {
      const response = await api.get(`/vendors/${vendorId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching vendor profile:', error);
      throw error;
    }
  },

  // Get vendor profile by email (for main auth system integration)
  getProfileByEmail: async (email) => {
    try {
      const response = await api.get(`/vendors/by-email/${encodeURIComponent(email)}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching vendor profile by email:', error);
      throw error;
    }
  },

  // Get all vendors (for testing/admin purposes)
  getAllVendors: async () => {
    try {
      const response = await api.get('/vendors');
      return response.data;
    } catch (error) {
      console.error('Error fetching vendors:', error);
      throw error;
    }
  },

  // Bid management
  createBid: async (bidData) => {
    try {
      const response = await api.post('/vendors/bids', bidData);
      return response.data;
    } catch (error) {
      console.error('Error creating bid:', error);
      throw error;
    }
  },

  // Get vendor's bids
  getVendorBids: async (vendorId, filters = {}) => {
    try {
      const params = { vendorId, ...filters };
      const response = await api.get('/vendors/bids/all', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching vendor bids:', error);
      throw error;
    }
  },

  // Get all bids (with optional filters)
  getAllBids: async (filters = {}) => {
    try {
      const response = await api.get('/vendors/bids/all', { params: filters });
      return response.data;
    } catch (error) {
      console.error('Error fetching bids:', error);
      throw error;
    }
  },

  // Get specific bid
  getBid: async (bidId) => {
    try {
      const response = await api.get(`/vendors/bids/${bidId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching bid:', error);
      throw error;
    }
  },

  // Update bid
  updateBid: async (bidId, bidData) => {
    try {
      const response = await api.put(`/vendors/bids/${bidId}`, bidData);
      return response.data;
    } catch (error) {
      console.error('Error updating bid:', error);
      throw error;
    }
  },

  // Update bid status
  updateBidStatus: async (bidId, status, reason = '') => {
    try {
      const response = await api.put(`/vendors/bids/${bidId}/status`, {
        status,
        reason
      });
      return response.data;
    } catch (error) {
      console.error('Error updating bid status:', error);
      throw error;
    }
  },

  // Get vendor statistics
  getVendorStats: async (vendorId) => {
    try {
      const bids = await vendorApi.getVendorBids(vendorId);
      
      const stats = {
        totalBids: bids.length,
        draftBids: bids.filter(bid => bid.status === 'draft').length,
        submittedBids: bids.filter(bid => bid.status === 'submitted').length,
        approvedBids: bids.filter(bid => bid.status === 'approved').length,
        rejectedBids: bids.filter(bid => bid.status === 'rejected').length,
        totalValue: bids.reduce((sum, bid) => sum + parseFloat(bid.totalBidAmount || 0), 0),
        approvedValue: bids
          .filter(bid => bid.status === 'approved')
          .reduce((sum, bid) => sum + parseFloat(bid.totalBidAmount || 0), 0)
      };

      return stats;
    } catch (error) {
      console.error('Error calculating vendor stats:', error);
      return {
        totalBids: 0,
        draftBids: 0,
        submittedBids: 0,
        approvedBids: 0,
        rejectedBids: 0,
        totalValue: 0,
        approvedValue: 0
      };
    }
  },

  // ========== Phase 2: AI & ML Features ==========
  
  // AI Document Parsing
  parseDocuments: async (formData) => {
    try {
      const response = await api.post('/ai/parse-documents', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error parsing documents:', error);
      throw error;
    }
  },

  // AI Bid Analysis
  analyzeBid: async (bidData) => {
    try {
      const response = await api.post('/ai/analyze-bid', bidData);
      return response.data;
    } catch (error) {
      console.error('Error analyzing bid:', error);
      throw error;
    }
  },

  // Get ML recommendations for vendor
  getMLRecommendations: async (vendorId, projectRequirements = {}) => {
    try {
      const response = await api.post('/recommendation/vendor-recommendations', {
        vendorId,
        projectRequirements,
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching ML recommendations:', error);
      throw error;
    }
  },

  // Get vendor performance prediction
  getPerformancePrediction: async (vendorId) => {
    try {
      const response = await api.get(`/ai/predict-vendor-performance/${vendorId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching performance prediction:', error);
      throw error;
    }
  },

  // Get pricing predictions
  getPricePredictions: async (costItemId, projectParams = {}) => {
    try {
      const response = await api.post('/ai/predict-price', {
        costItemId,
        projectParams,
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching price predictions:', error);
      throw error;
    }
  },

  // Get bid insights for cost item
  getBidInsights: async (costItemId) => {
    try {
      const response = await api.get(`/ai/bid-insights/${costItemId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching bid insights:', error);
      throw error;
    }
  },

  // ========== Real-time Features ==========
  
  // Get real-time bid analytics
  getBidAnalytics: async (vendorId, timeRange = 30) => {
    try {
      const response = await api.get('/websocket/bid-analytics', {
        params: { vendorId, timeRange },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching bid analytics:', error);
      throw error;
    }
  },

  // ========== Security Features ==========
  
  // Get MFA status
  getMFAStatus: async () => {
    try {
      const response = await api.get('/security/mfa/status');
      return response.data;
    } catch (error) {
      console.error('Error fetching MFA status:', error);
      throw error;
    }
  },

  // Setup MFA
  setupMFA: async () => {
    try {
      const response = await api.post('/security/mfa/setup');
      return response.data;
    } catch (error) {
      console.error('Error setting up MFA:', error);
      throw error;
    }
  },

  // Verify MFA token
  verifyMFA: async (token) => {
    try {
      const response = await api.post('/security/mfa/verify', { token });
      return response.data;
    } catch (error) {
      console.error('Error verifying MFA:', error);
      throw error;
    }
  }
};

export default vendorApi;